import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import useAuthStore from '../store/authStore'
import { hasAdminPermission } from '../utils/adminPermissions'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { adminOrderService } from '../services'
import AdminShopDepositUploadDialog from '../components/orders/AdminShopDepositUploadDialog'
import { getApiErrorMessage } from '../services/apiClient'
import { formatDateTime } from '../utils/dateFormat'
import { rangeForDashboardPeriod, toYmd } from '../utils/undeliveredQuantityFilters'
import TableEmptyState from '../components/shared/TableEmptyState'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import { downloadBlobResponse } from '../components/price-concerns/priceConcernUtils'

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '3d', label: 'Last 3 Days' },
  { value: '7d', label: 'Last 7 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'custom', label: 'Custom Range' },
]

const headerCellSx = { fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }

const paginationSx = {
  width: '100%',
  overflowX: 'auto',
  '& .MuiToolbar-root': {
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    minHeight: { xs: 48, sm: 52 },
    px: { xs: 0.5, sm: 2 },
    gap: 0.5,
  },
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Awaiting verification' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'REJECTED', label: 'Rejected' },
]

function formatAmount(value) {
  const n = Number(value ?? 0)
  return `৳${n.toFixed(2)}`
}

function depositStatusChip(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'VERIFIED') {
    return (
      <Chip label="Verified" color="success" size="small" variant="outlined" />
    )
  }
  if (s === 'REJECTED') {
    return (
      <Chip label="Rejected" color="error" size="small" variant="outlined" />
    )
  }
  return (
    <Chip
      label="Awaiting verification"
      color="warning"
      size="small"
      variant="outlined"
    />
  )
}

function submittedRangeForPreset(datePreset, dateFrom, dateTo) {
  if (datePreset === 'custom') {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return {}
    return { submitted_from: dateFrom, submitted_to: dateTo }
  }
  if (datePreset === '3d') {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const from = new Date(today)
    from.setDate(from.getDate() - 2)
    return { submitted_from: toYmd(from), submitted_to: toYmd(today) }
  }
  const periodMap = {
    today: 'today',
    '7d': '7d',
    month: 'month',
    quarter: 'quarter',
  }
  const range = rangeForDashboardPeriod(
    periodMap[datePreset] || '7d',
    {},
    new Date(),
  )
  return { submitted_from: range.dateFrom, submitted_to: range.dateTo }
}

export default function AdminDepositsListPage() {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canUploadDeposit = hasAdminPermission(
    adminPermissions,
    'order',
    'startProcurement',
  )
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [searchParams, setSearchParams] = useSearchParams()

  const page = Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0)
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(
    parseInt(searchParams.get('limit') || '25', 10),
  )
    ? parseInt(searchParams.get('limit') || '25', 10)
    : 25
  const search = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || ''
  const datePreset = searchParams.get('datePreset') || '7d'
  const dateFrom = searchParams.get('dateFrom') || ''
  const dateTo = searchParams.get('dateTo') || ''

  const customReady =
    datePreset !== 'custom' ||
    (Boolean(dateFrom) && Boolean(dateTo) && dateFrom <= dateTo)

  const [searchInput, setSearchInput] = useState(search)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [shwapnoReceivingAccounts, setShwapnoReceivingAccounts] = useState([])
  const [uploadSuccess, setUploadSuccess] = useState(null)

  const replaceParams = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === '' || value == null) next.delete(key)
        else next.set(key, String(value))
      })
      setSearchParams(next)
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== search) {
        replaceParams({ search: searchInput.trim(), page: 0 })
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, search, replaceParams])

  const listQueryParams = useMemo(() => {
    const base = {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...submittedRangeForPreset(datePreset, dateFrom, dateTo),
    }
    return base
  }, [statusFilter, search, datePreset, dateFrom, dateTo])

  const load = useCallback(async () => {
    if (!customReady) {
      setLoading(false)
      setItems([])
      setTotal(0)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const res = await adminOrderService.listAdminDeposits({
        page: page + 1,
        limit: rowsPerPage,
        ...listQueryParams,
      })
      setItems(res?.items ?? [])
      setTotal(res?.pagination?.total ?? 0)
    } catch (err) {
      setItems([])
      setTotal(0)
      setError(getApiErrorMessage(err, 'Failed to load deposits'))
    } finally {
      setLoading(false)
    }
  }, [customReady, page, rowsPerPage, listQueryParams])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!uploadOpen) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const res = await adminOrderService.getPaymentProofReceivingAccounts()
        if (cancelled) return
        const accounts = res?.data ?? []
        setShwapnoReceivingAccounts(Array.isArray(accounts) ? accounts : [])
      } catch {
        if (!cancelled) setShwapnoReceivingAccounts([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [uploadOpen])

  const handleExportExcel = async () => {
    if (exporting || !customReady || total === 0) return
    try {
      setExporting(true)
      const response = await adminOrderService.exportAdminDepositsExcel(
        listQueryParams,
      )
      downloadBlobResponse(response, 'deposits.xlsx')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not export deposits.'))
    } finally {
      setExporting(false)
    }
  }

  const exportDisabled = exporting || loading || !customReady || total === 0

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/orders/purchase')}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Purchase orders
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Stack spacing={0.5}>
          <Typography variant="h5" fontWeight={700}>Deposits</Typography>
          <Typography variant="body2" color="text.secondary">
            View shop payment proof deposits (with or without a linked order). Open a
            deposit to verify, reject, edit after rejection, or reverse — same as on
            the order page.
          </Typography>
        </Stack>
        {canUploadDeposit ? (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              bgcolor: '#11809A',
              '&:hover': { bgcolor: '#0f6d82' },
            }}
          >
            Upload deposit
          </Button>
        ) : null}
      </Stack>

      {uploadSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>
          {uploadSuccess}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 2, p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              size="small"
              label="Search deposit ID or order ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: { xs: '100%', md: 280 }, flex: { md: '1 1 280px' } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 } }}>
              <InputLabel id="deposits-status-filter">Status</InputLabel>
              <Select
                labelId="deposits-status-filter"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  replaceParams({ status: e.target.value, page: 0 })
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              select
              label="Period"
              size="small"
              value={datePreset}
              onChange={(event) => {
                const next = event.target.value
                replaceParams({
                  datePreset: next,
                  dateFrom: next === 'custom' ? dateFrom : '',
                  dateTo: next === 'custom' ? dateTo : '',
                  page: 0,
                })
              }}
              sx={{ minWidth: { xs: '100%', md: 170 } }}
            >
              {DATE_PRESETS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {datePreset === 'custom' && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={dateFrom}
                  onChange={(event) =>
                    replaceParams({ dateFrom: event.target.value, page: 0 })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', md: 160 } }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={dateTo}
                  onChange={(event) =>
                    replaceParams({ dateTo: event.target.value, page: 0 })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', md: 160 } }}
                />
              </>
            )}
          </Stack>
          {datePreset === 'custom' && !customReady && (
            <Typography variant="caption" color="text.secondary">
              Choose a start and end date. The start must be on or before the end.
            </Typography>
          )}
        </Stack>
      </Card>

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total records: {total}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={exportDisabled}
            onClick={handleExportExcel}
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              borderColor: '#e90000',
              color: '#e90000',
              '&:hover': { borderColor: '#c40000', bgcolor: 'rgba(233,0,0,0.04)' },
            }}
          >
            {exporting ? 'Exporting…' : 'Download Excel'}
          </Button>
        </Box>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size={isCompact ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                <TableCell sx={headerCellSx}>Deposit</TableCell>
                <TableCell sx={headerCellSx}>Shop</TableCell>
                <TableCell sx={headerCellSx}>Order</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                <TableCell sx={headerCellSx}>Submitted</TableCell>
                <TableCell sx={headerCellSx}>Rejection reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableLoadingRow colSpan={7} />
              ) : items.length === 0 ? (
                <TableEmptyState colSpan={7} message="No deposits found." />
              ) : (
                items.map((row) => (
                  <TableRow key={row.request_id} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/orders/purchase/deposits/${row.request_id}`}
                        fontWeight={600}
                        color="#e90000"
                        underline="hover"
                      >
                        {row.deposit_id}
                      </Link>
                    </TableCell>
                    <TableCell>{row.shop_name || row.organization_id || '—'}</TableCell>
                    <TableCell>
                      {row.order_id ? (
                        <Link
                          component={RouterLink}
                          to={`/orders/${row.order_id}`}
                          color="#e90000"
                          underline="hover"
                        >
                          {row.order_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{depositStatusChip(row.status)}</TableCell>
                    <TableCell align="right">{formatAmount(row.amount)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.submitted_at)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                      {row.rejection_reason || '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => replaceParams({ page: newPage })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            replaceParams({
              limit: parseInt(e.target.value, 10),
              page: 0,
            })
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={paginationSx}
        />
      </Card>

      <AdminShopDepositUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        shwapnoAccounts={shwapnoReceivingAccounts}
        onUploaded={async (result) => {
          const depositId = result?.deposit_id || result?.request_id
          setUploadSuccess(
            depositId
              ? `Deposit ${typeof depositId === 'string' ? depositId : `DEP-${depositId}`} submitted for verification.`
              : 'Deposit submitted for verification.',
          )
          await load()
        }}
      />
    </Box>
  )
}
